import React, { useState } from 'react';
import { ShareNetwork, Copy, WhatsappLogo, LinkedinLogo, XLogo, CheckCircle } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ShareResult({ results }) {
  const [copied, setCopied] = useState(false);

  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/?d=${Math.round(results.distance_km)}&o=${results.office_days}&m=${Math.round(results.minutes_one_way)}`
    : '';

  const ogImageUrl = `${API}/og-image?score=${results.score}&hours=${Math.round(results.annual_hours)}&days=${results.annual_days.toFixed(1)}&cost=${Math.round(results.annual_cost)}`;

  const shareText =
    results.score < 40
      ? `Mein Pendel-Score: ${results.score}/100 — ich verliere ${Math.round(results.annual_hours)} h Lebenszeit pro Jahr im Auto. Berechne deinen:`
      : results.score < 70
      ? `Mein Arbeitsweg kostet mich ${results.annual_days.toFixed(1)} Tage Lebenszeit pro Jahr. Wie viel kostet dich deiner?`
      : `Mein Pendel-Score: ${results.score}/100. Mein Arbeitsweg ist überraschend effizient. Check deinen:`;

  async function nativeShare() {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'PendelPilot', text: shareText, url: shareUrl });
      } catch (e) {
        // user cancelled or unsupported
      }
    } else {
      copyLink();
    }
  }

  async function copyLink() {
    const text = `${shareText} ${shareUrl}`;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback: hidden textarea + execCommand
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      // Permission denied (e.g. restricted iframe). Show subtle indicator only.
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }
  }

  const wa = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
  const li = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;

  return (
    <div className="mt-12 grid-border" data-testid="share-block">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
        <div className="lg:col-span-3 p-6 md:p-10">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Teilen</div>
          <h3 className="font-display text-3xl md:text-4xl tracking-tighter mt-2">
            Zeig deinen Pendel-Score.
          </h3>
          <p className="text-neutral-700 mt-3 max-w-md">
            Jeder kennt jemanden, der zu viel pendelt. Teile dein Ergebnis — vielleicht öffnet es jemandem die Augen.
          </p>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="share-buttons">
            <button
              data-testid="share-native"
              onClick={nativeShare}
              className="h-12 px-5 bg-neutral-900 text-white text-xs uppercase tracking-widest hover:bg-black inline-flex items-center gap-2"
            >
              <ShareNetwork size={16} weight="bold" /> Teilen
            </button>
            <a
              data-testid="share-whatsapp"
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              className="h-12 px-4 border border-neutral-900 text-neutral-900 text-xs uppercase tracking-widest hover:bg-neutral-900 hover:text-white inline-flex items-center gap-2"
            >
              <WhatsappLogo size={16} weight="bold" /> WhatsApp
            </a>
            <a
              data-testid="share-linkedin"
              href={li}
              target="_blank"
              rel="noopener noreferrer"
              className="h-12 px-4 border border-neutral-900 text-neutral-900 text-xs uppercase tracking-widest hover:bg-neutral-900 hover:text-white inline-flex items-center gap-2"
            >
              <LinkedinLogo size={16} weight="bold" /> LinkedIn
            </a>
            <a
              data-testid="share-x"
              href={tw}
              target="_blank"
              rel="noopener noreferrer"
              className="h-12 px-4 border border-neutral-900 text-neutral-900 text-xs uppercase tracking-widest hover:bg-neutral-900 hover:text-white inline-flex items-center gap-2"
            >
              <XLogo size={16} weight="bold" /> X
            </a>
            <button
              data-testid="share-copy"
              onClick={copyLink}
              className="h-12 px-4 border border-neutral-300 text-neutral-700 text-xs uppercase tracking-widest hover:bg-neutral-100 inline-flex items-center gap-2"
            >
              {copied ? <CheckCircle size={16} weight="bold" /> : <Copy size={16} weight="bold" />}
              {copied ? 'Kopiert' : 'Link kopieren'}
            </button>
          </div>
        </div>
        <div className="lg:col-span-2 border-t lg:border-t-0 lg:border-l border-neutral-200 bg-neutral-50 p-6">
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3">Vorschau-Karte</div>
          <div className="border border-neutral-300 overflow-hidden bg-white">
            <img
              src={ogImageUrl}
              alt="Mein Pendel-Score"
              loading="lazy"
              className="w-full h-auto block"
              data-testid="share-preview"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
